import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from './button';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ImageUpload = ({ 
  value = [], 
  onChange, 
  maxFiles = 5,
  language = 'en',
  className = "",
  "data-testid": testId = "image-upload"
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const labels = {
    en: {
      dropHere: 'Drop images here or click to upload',
      maxFiles: `Max ${maxFiles} images`,
      allowedFormats: 'JPG, JPEG, PNG only (max 5MB)',
      uploading: 'Uploading...',
      remove: 'Remove'
    },
    fr: {
      dropHere: 'Déposez les images ici ou cliquez pour télécharger',
      maxFiles: `Max ${maxFiles} images`,
      allowedFormats: 'JPG, JPEG, PNG uniquement (max 5Mo)',
      uploading: 'Téléchargement...',
      remove: 'Supprimer'
    }
  };

  const t = labels[language] || labels.en;

  const handleFiles = async (files) => {
    const fileList = Array.from(files).slice(0, maxFiles - value.length);
    if (fileList.length === 0) return;

    setUploading(true);
    const newImages = [...value];

    try {
      for (const file of fileList) {
        // Validate file type
        if (!file.type.startsWith('image/')) continue;
        
        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) continue;

        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API_URL}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.url) {
          // Construct full URL for the uploaded image with /api prefix
          const baseUrl = process.env.REACT_APP_BACKEND_URL;
          // Store full URL to work across the app - use /api/uploads route
          const fullUrl = baseUrl + '/api' + response.data.url;
          newImages.push(fullUrl);
        }
      }

      onChange(newImages);
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleRemoveImage = (index) => {
    const newImages = value.filter((_, i) => i !== index);
    onChange(newImages);
  };

  const handleClick = () => {
    if (!uploading && value.length < maxFiles) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={className} data-testid={testId}>
      {/* Image Previews */}
      {value.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {value.map((url, index) => (
            <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-border">
              <img
                src={url}
                alt={`Upload ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => handleRemoveImage(index)}
                className="absolute top-1 right-1 p-1 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-image-${index}`}
              >
                <X className="h-3 w-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {value.length < maxFiles && (
        <div
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}
            ${uploading ? 'pointer-events-none opacity-50' : ''}
          `}
          data-testid="upload-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid="file-input"
          />
          
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">{t.uploading}</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Upload className="h-6 w-6 text-primary" />
              </div>
              <p className="text-sm text-foreground">{t.dropHere}</p>
              <p className="text-xs text-primary font-medium">{t.allowedFormats}</p>
              <p className="text-xs text-muted-foreground">{t.maxFiles}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
